<template>
  <v-container fill-height :style="{
    backgroundColor: colors.blue,
    margin: 0,
    minWidth: '100%',
    position: 'relative'
  }">
    <!-- Language Switcher - Top Right -->
    <div style="position: absolute; top: 20px; right: 20px; z-index: 10;">
      <LanguageSwitcher />
    </div>

    <div :style="{
      backgroundImage: 'url(/assets/bg.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      opacity: 0.1
    }"></div>

    <v-row justify="center" align="center" class="fill-height">
      <v-col cols="12" sm="8" md="6" lg="4">
        <v-card class="pa-6" elevation="8">
          <v-card-title class="text-center mb-4">
            <h2>{{ $t('resetPassword.title') }}</h2>
          </v-card-title>

          <v-form ref="resetForm" @submit.prevent="resetPassword">
            <v-text-field
              v-model="email"
              :label="$t('resetPassword.email')"
              type="email"
              variant="outlined"
              prepend-inner-icon="mdi-email"
              readonly
              class="mb-3"
            ></v-text-field>

            <v-text-field
              v-model="password"
              :label="$t('resetPassword.newPassword')"
              :type="showPassword ? 'text' : 'password'"
              variant="outlined"
              prepend-inner-icon="mdi-lock"
              :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
              @click:append-inner="showPassword = !showPassword"
              :rules="passwordRules"
              :error-messages="passwordErrors"
              required
              @input="clearPasswordError"
              class="mb-3"
            ></v-text-field>

            <v-text-field
              v-model="passwordConfirmation"
              :label="$t('resetPassword.confirmPassword')"
              :type="showPasswordConfirmation ? 'text' : 'password'"
              variant="outlined"
              prepend-inner-icon="mdi-lock-check"
              :append-inner-icon="showPasswordConfirmation ? 'mdi-eye' : 'mdi-eye-off'"
              @click:append-inner="showPasswordConfirmation = !showPasswordConfirmation"
              :rules="confirmRules"
              :error-messages="confirmErrors"
              required
              @input="clearConfirmError"
              class="mb-3"
            ></v-text-field>

            <v-alert v-if="successMessage" type="success" class="mb-4">
              {{ successMessage }}
            </v-alert>

            <v-alert v-if="errorMessage" type="error" class="mb-4" closable @click:close="errorMessage = ''">
              {{ errorMessage }}
            </v-alert>

            <v-btn
              type="submit"
              :color="colors.primary"
              block
              size="large"
              :loading="loading"
              :disabled="loading || !!successMessage"
              class="mb-3"
            >
              {{ $t('resetPassword.resetButton') }}
            </v-btn>

            <v-btn
              v-if="successMessage"
              @click="goToLogin"
              :color="colors.primary"
              block
              size="large"
              variant="outlined"
            >
              {{ $t('resetPassword.backToLogin') }}
            </v-btn>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios';
import api from '../api';
import colors from '../styles/colors';
import LanguageSwitcher from './LanguageSwitcher.vue';

export default {
  name: 'JuryResetPassword',
  components: {
    LanguageSwitcher,
  },
  data() {
    return {
      colors,
      token: '',
      email: '',
      password: '',
      passwordConfirmation: '',
      showPassword: false,
      showPasswordConfirmation: false,
      loading: false,
      successMessage: '',
      errorMessage: '',
      passwordErrors: [],
      confirmErrors: [],
    };
  },
  computed: {
    passwordRules() {
      return [
        v => !!v || this.$t('resetPassword.passwordRequired'),
        v => (v && v.length >= 8) || this.$t('resetPassword.passwordMinLength'),
      ];
    },
    confirmRules() {
      return [
        v => !!v || this.$t('resetPassword.confirmRequired'),
        v => v === this.password || this.$t('resetPassword.passwordMismatch'),
      ];
    },
  },
  methods: {
    clearPasswordError() {
      this.passwordErrors = [];
      this.errorMessage = '';
    },
    clearConfirmError() {
      this.confirmErrors = [];
      this.errorMessage = '';
    },

    async resetPassword() {
      this.successMessage = '';
      this.errorMessage = '';
      this.passwordErrors = [];
      this.confirmErrors = [];

      const { valid } = await this.$refs.resetForm.validate();
      if (!valid) {
        return;
      }

      if (this.password !== this.passwordConfirmation) {
        this.confirmErrors = [this.$t('resetPassword.passwordMismatch')];
        return;
      }

      this.loading = true;

      try {
        const response = await axios.post(api.juryResetPassword, {
          token: this.token,
          email: this.email,
          password: this.password,
          password_confirmation: this.passwordConfirmation,
        });

        if (response.data.success) {
          this.successMessage = this.$t('resetPassword.successMessage');

          // Redirect to login after 3 seconds
          setTimeout(() => {
            this.goToLogin();
          }, 3000);
        }
      } catch (error) {
        console.error('Reset password error:', error);

        if (error.response) {
          if (error.response.status === 422) {
            const errors = error.response.data.errors;
            if (errors) {
              if (errors.password) {
                this.passwordErrors = Array.isArray(errors.password) ? errors.password : [errors.password];
              }
              if (errors.token) {
                this.errorMessage = this.$t('resetPassword.invalidLink');
              }
            }
            this.errorMessage = this.errorMessage || error.response.data.message || this.$t('resetPassword.validationError');
          } else {
            this.errorMessage = error.response.data.message || this.$t('resetPassword.resetError');
          }
        } else {
          this.errorMessage = this.$t('resetPassword.serverError');
        }
      } finally {
        this.loading = false;
      }
    },

    goToLogin() {
      this.$router.push({ name: 'JuryLogin' });
    },
  },
  mounted() {
    // Get token and email from URL query parameters
    this.token = this.$route.query.token || '';
    this.email = this.$route.query.email || '';

    if (!this.token || !this.email) {
      this.errorMessage = this.$t('resetPassword.invalidLinkError');
    }
  },
};
</script>

<style scoped>
.fill-height {
  min-height: 100vh;
}
</style>